import { useState, useCallback } from 'react';
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useTranslation } from 'react-i18next';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';

/**
 * Format Korean Won price with commas and no decimals
 * @param price - Price in KRW
 * @returns Formatted string with commas
 */
function formatKRW(price: number): string {
  return new Intl.NumberFormat('ko-KR', {
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format USD price with commas and 2 decimals
 * @param price - Price in USD
 * @returns Formatted string with 2 decimal places
 */
function formatUSD(price: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(price);
}

/**
 * Format KRW/USD rate - shows 2 decimals, omits .00 for whole numbers
 * @param price - Exchange rate
 * @returns Formatted string with optional decimals
 */
function formatRate(price: number): string {
  const formatted = new Intl.NumberFormat('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

  // Remove .00 or ,00 at the end for whole numbers
  return formatted.replace(/[.,]00$/, '');
}

/**
 * Props for HeaderStats component
 */
interface HeaderStatsProps {
  /** Whether the stats are minimized */
  isMinimized?: boolean;
  /** Callback when minimize state changes */
  onMinimizeChange?: (minimized: boolean) => void;
}

/**
 * HeaderStats - Display key market prices at a glance
 *
 * Shows real-time BTC prices from:
 * - Upbit (KRW)
 * - Bithumb (KRW)
 * - Binance (USDT)
 *
 * Features:
 * - Real-time WebSocket price updates (75ms ticker)
 * - Responsive layout (horizontal desktop, vertical mobile)
 * - Cyberpunk theme with glass effect cards
 * - Minimizable view with inline KRW/USDT rates
 */
export const HeaderStats: React.FC<HeaderStatsProps> = ({
  isMinimized: externalMinimized,
  onMinimizeChange,
}) => {
  const { t } = useTranslation();
  const prices = usePriceUpdates();
  const [internalMinimized, setInternalMinimized] = useState(false);

  // Use external control if provided, otherwise use internal state
  const isMinimized = externalMinimized !== undefined ? externalMinimized : internalMinimized;

  const handleToggleMinimize = useCallback(() => {
    const newValue = !isMinimized;
    if (onMinimizeChange) {
      onMinimizeChange(newValue);
    } else {
      setInternalMinimized(newValue);
    }
  }, [isMinimized, onMinimizeChange]);

  // Loading state - don't render until we have price data
  if (!prices) {
    return null;
  }

  // Calculate implied KRW/USDT rate (Upbit BTC KRW / Binance BTC USDT)
  const upbitImpliedRate = prices.upbitBtcKrw && prices.binanceBtcUsdt
    ? prices.upbitBtcKrw / prices.binanceBtcUsdt
    : null;

  const bithumbImpliedRate = prices.bithumbBtcKrw && prices.binanceBtcUsdt
    ? prices.bithumbBtcKrw / prices.binanceBtcUsdt
    : null;

  // Use official USD/KRW rate from Naver Finance
  const officialRate = prices.officialUsdKrw;

  // Calculate kimchi premium percentage for Upbit
  const upbitKimchiPremium = upbitImpliedRate && officialRate
    ? ((upbitImpliedRate / officialRate) - 1) * 100
    : null;

  // Calculate kimchi premium percentage for Bithumb
  const bithumbKimchiPremium = bithumbImpliedRate && officialRate
    ? ((bithumbImpliedRate / officialRate) - 1) * 100
    : null;

  // When minimized, return null - parent component will show HeaderStatsInline
  if (isMinimized) {
    return null;
  }

  // Expanded view - full stats table
  return (
    <Paper
      elevation={4}
      sx={{
        my: 2.25,
        mx: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.05) 0%, rgba(255, 0, 255, 0.05) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(0, 255, 255, 0.2)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, position: 'relative' }}>
        {/* Minimize Button */}
        <Tooltip title={t('headerStats.minimize')} arrow>
          <IconButton
            onClick={handleToggleMinimize}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'rgba(0, 255, 255, 0.7)',
              '&:hover': {
                color: 'rgba(0, 255, 255, 1)',
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
              },
            }}
          >
            <UnfoldLessIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Header Row with Exchange Logos */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '50px repeat(3, 1fr)', sm: '60px repeat(3, 1fr)', md: '80px repeat(3, 1fr)' },
            gap: 2,
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Exchange Label */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.85rem',
              textAlign: 'center',
            }}
          >
            {t('headerStats.exchange')}
          </Typography>

          {/* Upbit Header */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src="/logos/upbit.png"
              alt="Upbit"
              sx={{
                height: 22,
                width: 'auto',
              }}
            />
          </Box>

          {/* Binance Header */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src="/logos/binance.png"
              alt="Binance"
              sx={{
                height: 22,
                width: 'auto',
              }}
            />
          </Box>

          {/* Bithumb Header */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src="/logos/bithumb.ico"
              alt="Bithumb"
              sx={{
                height: 22,
                width: 'auto',
              }}
            />
          </Box>
        </Box>

        {/* BTC Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '50px repeat(3, 1fr)', sm: '60px repeat(3, 1fr)', md: '80px repeat(3, 1fr)' },
            gap: 2,
            mb: 1.5,
            alignItems: 'center',
          }}
        >
          {/* BTC Label */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            BTC
          </Typography>

          {/* Upbit BTC */}
          <Box sx={{ textAlign: 'center' }}>
            {prices.upbitBtcKrw ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ₩{formatKRW(prices.upbitBtcKrw)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Binance BTC */}
          <Box sx={{ textAlign: 'center' }}>
            {prices.binanceBtcUsdt ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ${formatUSD(prices.binanceBtcUsdt)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Bithumb BTC */}
          <Box sx={{ textAlign: 'center' }}>
            {prices.bithumbBtcKrw ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ₩{formatKRW(prices.bithumbBtcKrw)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>
        </Box>

        {/* USDT Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '50px repeat(3, 1fr)', sm: '60px repeat(3, 1fr)', md: '80px repeat(3, 1fr)' },
            gap: 2,
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            alignItems: 'center',
          }}
        >
          {/* USDT Label */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            USDT
          </Typography>

          {/* Upbit USDT */}
          <Box sx={{ textAlign: 'center' }}>
            {prices.upbitUsdtKrw ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ₩{formatKRW(prices.upbitUsdtKrw)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Implied KRW/USDT Rate (Binance column) */}
          <Box sx={{ textAlign: 'center' }}>
            {upbitImpliedRate !== null ? (
              <>
                {/* Desktop: 2 decimal places (omits .00) */}
                <Typography
                  variant="body1"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    color: 'text.primary',
                    fontSize: '0.95rem',
                  }}
                >
                  ₩{formatRate(upbitImpliedRate)}
                </Typography>
                {/* Mobile/Tablet: 0 decimal places */}
                <Typography
                  variant="body1"
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    color: 'text.primary',
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  }}
                >
                  ₩{formatKRW(upbitImpliedRate)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Bithumb USDT */}
          <Box sx={{ textAlign: 'center' }}>
            {prices.bithumbUsdtKrw ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ₩{formatKRW(prices.bithumbUsdtKrw)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>
        </Box>

        {/* Exchange Rate Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '50px repeat(3, 1fr)', sm: '60px repeat(3, 1fr)', md: '80px repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          {/* Rate Label */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            {t('headerStats.officialRate')}
          </Typography>

          {/* Upbit Kimchi Premium */}
          <Box sx={{ textAlign: 'center' }}>
            {upbitKimchiPremium !== null ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: upbitKimchiPremium >= 0 ? 'success.main' : 'error.main',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                  fontWeight: 600,
                }}
              >
                {upbitKimchiPremium >= 0 ? '+' : ''}{upbitKimchiPremium.toFixed(2)}%
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Official Rate */}
          <Box sx={{ textAlign: 'center' }}>
            {officialRate ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                }}
              >
                ₩{formatRate(officialRate)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>

          {/* Bithumb Kimchi Premium */}
          <Box sx={{ textAlign: 'center' }}>
            {bithumbKimchiPremium !== null ? (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: bithumbKimchiPremium >= 0 ? 'success.main' : 'error.main',
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                  fontWeight: 600,
                }}
              >
                {bithumbKimchiPremium >= 0 ? '+' : ''}{bithumbKimchiPremium.toFixed(2)}%
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">-</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Props for inline header stats (shown in title bar when minimized)
 */
interface HeaderStatsInlineProps {
  /** Callback when expand button is clicked */
  onExpand: () => void;
}

/**
 * HeaderStatsInline - Compact inline display for the title bar
 *
 * Shows only the essential KRW/USDT rate information:
 * - Calculated implied rate from BTC prices (Upbit BTC KRW / Binance BTC USDT)
 * - Upbit USDT/KRW price
 * - Expand button to show full stats
 */
export const HeaderStatsInline: React.FC<HeaderStatsInlineProps> = ({ onExpand }) => {
  const { t } = useTranslation();
  const prices = usePriceUpdates();

  // Don't render if no prices
  if (!prices) {
    return null;
  }

  // Calculate implied KRW/USDT rate from BTC prices
  const upbitImpliedRate = prices.upbitBtcKrw && prices.binanceBtcUsdt
    ? prices.upbitBtcKrw / prices.binanceBtcUsdt
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 1, sm: 1.5 },
        py: 0.5,
        borderRadius: 1,
        background: 'rgba(0, 255, 255, 0.05)',
        border: '1px solid rgba(0, 255, 255, 0.2)',
      }}
    >
      {/* Label */}
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          fontSize: { xs: '0.65rem', sm: '0.75rem' },
          display: { xs: 'none', sm: 'block' },
        }}
      >
        KRW/USDT
      </Typography>

      {/* Calculated Rate (from BTC prices) - shows Binance logo */}
      {upbitImpliedRate !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="img"
            src="/logos/binance.png"
            alt="Binance"
            sx={{ height: { xs: 12, sm: 14 }, width: 'auto' }}
          />
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              color: '#00ffff',
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              fontWeight: 600,
            }}
          >
            ₩{formatKRW(upbitImpliedRate)}
          </Typography>
        </Box>
      )}

      {/* Upbit USDT Rate */}
      {prices.upbitUsdtKrw && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="img"
            src="/logos/upbit.png"
            alt="Upbit"
            sx={{ height: { xs: 12, sm: 14 }, width: 'auto' }}
          />
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              color: 'text.primary',
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
            }}
          >
            ₩{formatKRW(prices.upbitUsdtKrw)}
          </Typography>
        </Box>
      )}

      {/* Expand Button */}
      <Tooltip title={t('headerStats.expand')} arrow>
        <IconButton
          onClick={onExpand}
          size="small"
          sx={{
            p: 0.25,
            color: 'rgba(0, 255, 255, 0.7)',
            '&:hover': {
              color: 'rgba(0, 255, 255, 1)',
              backgroundColor: 'rgba(0, 255, 255, 0.1)',
            },
          }}
        >
          <UnfoldMoreIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};